/**
 * Path: frontend/src/pages/SocialPage.jsx
 */
import { useEffect, useMemo, useState } from "react";
import {
  Bell,
  Briefcase,
  Eye,
  Film,
  Heart,
  Home,
  Image,
  Loader2,
  MessageCircle,
  Plus,
  Search,
  Send,
  Share2,
  Trash2,
  UserCheck,
  Users,
} from "lucide-react";
import apiClient from "../Api/apiConfig";
import { useAuth } from "../lib/auth";
import "../styles/social.css";

const API_ORIGIN =
  import.meta.env.VITE_API_ORIGIN ||
  (import.meta.env.VITE_API_URL?.startsWith("http")
    ? new URL(import.meta.env.VITE_API_URL).origin
    : window.location.origin);

const HELP_TYPES = [
  { value: "don", label: "Don" },
  { value: "demande", label: "Demande d'aide" },
  { value: "service", label: "Service solidaire" },
  { value: "aide_administrative", label: "Aide administrative" },
  { value: "transport", label: "Transport" },
  { value: "coup_de_main", label: "Coup de main" },
  { value: "autre", label: "Autre" },
];

const MARKET_CATEGORIES = [
  { value: "informatique", label: "Informatique" },
  { value: "telephones", label: "Téléphones" },
  { value: "meubles", label: "Meubles" },
  { value: "electromenager", label: "Électroménager" },
  { value: "jouets", label: "Jouets" },
  { value: "vetements", label: "Vêtements" },
  { value: "bebe_enfant", label: "Bébé / Enfant" },
  { value: "sport", label: "Sport" },
  { value: "livres_culture", label: "Livres / Culture" },
  { value: "auto_moto", label: "Auto / Moto" },
  { value: "maison_jardin", label: "Maison / Jardin" },
  { value: "autre", label: "Autre" },
];

const HOUSING_LISTING_TYPES = [
  { value: "rental_offer", label: "Offre de location" },
  { value: "rental_request", label: "Demande de location" },
  { value: "housing_exchange", label: "Échange de logement" },
];

const HOUSING_TYPES = [
  { value: "appartement", label: "Appartement" },
  { value: "studio", label: "Studio" },
  { value: "maison", label: "Maison" },
  { value: "chambre", label: "Chambre" },
  { value: "logement_social", label: "Logement social" },
  { value: "local_garage", label: "Local / garage" },
  { value: "autre", label: "Autre" },
];

const REACTIONS = [
  { id: "like", label: "J'aime", emoji: "👍", color: "#2563eb" },
  { id: "love", label: "J'adore", emoji: "❤️", color: "#ef4444" },
  { id: "solidarity", label: "Solidaire", emoji: "🥰", color: "#f59e0b" },
  { id: "haha", label: "Haha", emoji: "😂", color: "#f59e0b" },
  { id: "wow", label: "Wouah", emoji: "😮", color: "#f59e0b" },
  { id: "sad", label: "Triste", emoji: "😢", color: "#f59e0b" },
  { id: "angry", label: "Colère", emoji: "😡", color: "#ea580c" },
];

function mediaSrc(url) {
  if (!url) return "";
  if (
    url.startsWith("http://") ||
    url.startsWith("https://") ||
    url.startsWith("data:")
  ) {
    return url;
  }
  const cleanUrl = url.startsWith("/") ? url : `/${url}`;
  return `${API_ORIGIN}${cleanUrl}`;
}

function getDisplayName(user) {
  const fullName = [
    user?.first_name || user?.firstName,
    user?.last_name || user?.lastName,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  return (
    user?.display_name ||
    user?.displayName ||
    user?.name ||
    user?.full_name ||
    user?.username ||
    fullName ||
    user?.email ||
    "Membre NMS06"
  );
}

function getAuthorName(item) {
  return (
    item?.author_name ||
    item?.other_name ||
    item?.owner_name ||
    item?.display_name ||
    item?.name ||
    item?.author_email ||
    item?.other_email ||
    item?.email ||
    "Membre NMS06"
  );
}

function getAvatarUrl(item) {
  return (
    item?.avatar_url ||
    item?.author_avatar ||
    item?.other_avatar ||
    item?.owner_avatar ||
    item?.user_avatar ||
    ""
  );
}

function AvatarCircle({ src, name, size = 42, className = "" }) {
  const fallback = String(name || "N")
    .trim()
    .charAt(0)
    .toUpperCase();

  return (
    <div 
      className={`social-avatar-circle ${className}`}
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {src ? (
        <img src={mediaSrc(src)} alt={name || "Avatar"} />
      ) : (
        <span>{fallback || "N"}</span>
      )}
    </div>
  );
}

function formatDate(value) {
  if (!value) return "";
  return new Date(value).toLocaleString("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function EmptyState({ title, text }) {
  return (
    <div className="social-empty" style={{ textAlign: "center", padding: "40px 20px", color: "var(--social-text-muted)" }}>
      <p style={{ fontSize: "1.1rem", fontWeight: "600", marginBottom: "8px" }}>{title}</p>
      {text && <span>{text}</span>}
    </div>
  );
}

export default function SocialPage() {
  const { user } = useAuth();
  const displayName = getDisplayName(user);

  const [activeView, setActiveView] = useState("feed");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");

  const [posts, setPosts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);

  const [helpItems, setHelpItems] = useState([]);
  const [marketplaceSales, setMarketplaceSales] = useState([]);
  const [housingListings, setHousingListings] = useState([]);

  const [stripReels, setStripReels] = useState([]);
  const [selectedReel, setSelectedReel] = useState(null);
  const [isReelCreatorOpen, setIsReelCreatorOpen] = useState(false);

  const [commentsByPost, setCommentsByPost] = useState({});
  const [openComments, setOpenComments] = useState({});
  const [commentInputs, setCommentInputs] = useState({});

  const [postForm, setPostForm] = useState({
    content: "",
    file: null,
    preview: "",
    post_type: "post",
    group_id: "",
    visibility: "public",
  });

  const [reelForm, setReelForm] = useState({
    content: "",
    file: null,
    preview: "",
  });

  const [groupForm, setGroupForm] = useState({
    name: "",
    description: "",
    privacy: "public",
  });

  const [helpForm, setHelpForm] = useState({
    item_type: "demande",
    title: "",
    description: "",
    location: "",
    file: null,
    preview: "",
  });

  const [marketForm, setMarketForm] = useState({
    category: "autre",
    title: "",
    description: "",
    price: 0,
    location: "",
    item_condition: "bon_etat",
    file: null,
    preview: "",
  });

  const [housingForm, setHousingForm] = useState({
    listing_type: "rental_offer",
    housing_type: "appartement",
    title: "",
    description: "",
    location: "",
    surface_m2: "",
    rooms: "",
    rent_price: "",
    charges: "",
    deposit: "",
    available_from: "",
    current_location: "",
    desired_location: "",
    current_housing_type: "appartement",
    desired_housing_type: "appartement",
    current_surface_m2: "",
    desired_surface_m2: "",
    current_rooms: "",
    desired_rooms: "",
    exchange_reason: "",
    file: null,
    preview: "",
  });

  const [helpFilter, setHelpFilter] = useState("all");
  const [marketFilter, setMarketFilter] = useState("all");
  const [housingFilter, setHousingFilter] = useState("all");
  const [globalAdSearch, setGlobalAdSearch] = useState("");

  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isMarketModalOpen, setIsMarketModalOpen] = useState(false);
  const [isHousingModalOpen, setIsHousingModalOpen] = useState(false);

  const [editingAd, setEditingAd] = useState(null);

  const [sharingPost, setSharingPost] = useState(null);
  const [shareForm, setShareForm] = useState({
    target: "feed",
    content: "",
    group_id: "",
  });

  const [messageForm, setMessageForm] = useState({
    receiver_id: "",
    content: "",
  });

  const filteredHelp = useMemo(() => {
    return helpItems.filter(
      (item) =>
        (helpFilter === "all" || item.item_type === helpFilter) &&
        (!globalAdSearch ||
          String(item.title || "")
            .toLowerCase()
            .includes(globalAdSearch.toLowerCase()))
    );
  }, [helpItems, helpFilter, globalAdSearch]);

  const filteredMarket = useMemo(() => {
    return marketplaceSales.filter(
      (item) =>
        (marketFilter === "all" || item.category === marketFilter) &&
        (!globalAdSearch ||
          String(item.title || "")
            .toLowerCase()
            .includes(globalAdSearch.toLowerCase()))
    );
  }, [marketplaceSales, marketFilter, globalAdSearch]);

  const filteredHousing = useMemo(() => {
    return housingListings.filter(
      (item) =>
        (housingFilter === "all" || item.listing_type === housingFilter) &&
        (!globalAdSearch ||
          String(item.title || "")
            .toLowerCase()
            .includes(globalAdSearch.toLowerCase()))
    );
  }, [housingListings, housingFilter, globalAdSearch]);

  const reels = useMemo(
    () =>
      posts.filter(
        (post) =>
          post.post_type === "reel" ||
          post.media?.some((media) => media.media_type === "video")
      ),
    [posts]
  );

  const filteredPosts = useMemo(() => {
    const value = search.trim().toLowerCase();
    if (!value) return posts;

    return posts.filter((post) => {
      const author = getAuthorName(post).toLowerCase();
      return (
        author.includes(value) ||
        String(post.content || "").toLowerCase().includes(value) ||
        String(post.group_name || "").toLowerCase().includes(value)
      );
    });
  }, [posts, search]);

  const unreadCount = notifications.filter(
    (notification) => !notification.read_at
  ).length;

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError("");

      const [
        postsRes,
        groupsRes,
        helpRes,
        salesRes,
        housingRes,
        notificationsRes,
        conversationsRes,
        reelsRes,
      ] = await Promise.all([
        apiClient.get("/social/posts"),
        apiClient.get("/social/groups"),
        apiClient.get("/social/help"),
        apiClient.get("/social/marketplace-sales"),
        apiClient.get("/social/housing"),
        apiClient.get("/social/notifications"),
        apiClient.get("/social/messages/conversations"),
        apiClient.get("/social/posts?type=reel"),
      ]);

      setPosts(postsRes.data?.posts || []);
      setGroups(groupsRes.data?.groups || []);
      setHelpItems(helpRes.data?.items || []);
      setMarketplaceSales(salesRes.data?.items || []);
      setHousingListings(housingRes.data?.items || []);
      setNotifications(notificationsRes.data?.notifications || []);
      setConversations(conversationsRes.data?.conversations || []);
      setStripReels(reelsRes.data?.posts || []);
    } catch (err) {
      console.error("Erreur chargement réseau social :", err);
      setError("Impossible de charger le réseau NMS06 pour le moment.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const handleContactOwner = (item) => {
    setActiveView("messages");
    loadMessages(item.user_id);
    setMessageForm((prev) => ({
      ...prev,
      content: `Bonjour, je vous contacte à propos de votre annonce : "${item.title}".`,
    }));
  };

  const handlePostFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setPostForm((current) => ({
      ...current,
      file,
      preview: URL.createObjectURL(file),
      post_type: file.type.startsWith("video/") ? "reel" : current.post_type,
    }));
  };

  const resetPostForm = () => {
    if (postForm.preview) URL.revokeObjectURL(postForm.preview);

    setPostForm({
      content: "",
      file: null,
      preview: "",
      post_type: "post",
      group_id: "",
      visibility: "public",
    });
  };

  const submitPost = async (event) => {
    event.preventDefault();
    if (!postForm.content.trim() && !postForm.file) return;

    try {
      setActionLoading(true);

      let media = [];
      if (postForm.file) {
        console.log("UPLOAD POST FILE:", postForm.file);
        const formData = new FormData();
        formData.append("media", postForm.file);
        console.log("FORMDATA ENTRIES:", [...formData.entries()]);

        const uploadRes = await apiClient.post("/social/upload", formData);
        if (uploadRes.data?.media) media = [uploadRes.data.media];
      }

      const res = await apiClient.post("/social/posts", {
        content: postForm.content,
        post_type: postForm.post_type,
        visibility: postForm.group_id ? "group" : postForm.visibility,
        group_id: postForm.group_id || null,
        media,
      });

      setPosts((current) => [res.data.post, ...current]);

      if (res.data.post?.post_type === "reel") {
        setStripReels((current) => [res.data.post, ...current]);
      }

      resetPostForm();
    } catch (err) {
      console.error("Erreur publication :", err);
      alert("Publication impossible.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReelFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setReelForm((current) => ({
      ...current,
      file,
      preview: URL.createObjectURL(file),
    }));
  };

  const submitReel = async (event) => {
    event.preventDefault();

    if (!reelForm.file) {
      alert("Veuillez sélectionner une vidéo.");
      return;
    }

    try {
      setActionLoading(true);

      console.log("UPLOAD REEL FILE:", reelForm.file);
      const formData = new FormData();
      formData.append("media", reelForm.file);
      console.log("FORMDATA ENTRIES:", [...formData.entries()]);

      const uploadRes = await apiClient.post("/social/upload", formData);

      const res = await apiClient.post("/social/posts", {
        content: reelForm.content,
        post_type: "reel",
        visibility: "public",
        media: [uploadRes.data.media],
      });

      setStripReels((current) => [res.data.post, ...current]);
      setPosts((current) => [res.data.post, ...current]);
      setIsReelCreatorOpen(false);
      setReelForm({ content: "", file: null, preview: "" });

      alert("Votre Reel a été publié !");
    } catch (err) {
      console.error("Erreur reel :", err);
      alert("Erreur lors de la création du Reel.");
    } finally {
      setActionLoading(false);
    }
  };

  const deletePost = async (postId) => {
    if (!window.confirm("Supprimer cette publication ?")) return;

    await apiClient.delete(`/social/posts/${postId}`);

    setPosts((current) => current.filter((post) => post.id !== postId));
    setStripReels((current) => current.filter((post) => post.id !== postId));
  };

  const setReaction = async (post, reactionType) => {
    const wasLiked = !!post.my_reaction;
    const sameReaction = post.my_reaction === reactionType;

    setPosts((current) =>
      current.map((item) => {
        if (item.id !== post.id) return item;

        if (sameReaction) {
          return {
            ...item,
            my_reaction: null,
            has_liked: false,
            reaction_count: Math.max(0, Number(item.reaction_count || 0) - 1),
          };
        }

        return {
          ...item,
          my_reaction: reactionType,
          has_liked: true,
          reaction_count: wasLiked
            ? Number(item.reaction_count || 0)
            : Number(item.reaction_count || 0) + 1,
        };
      })
    );

    try {
      if (sameReaction) {
        await apiClient.delete(`/social/posts/${post.id}/reaction`);
      } else {
        await apiClient.post(`/social/posts/${post.id}/reaction`, {
          reaction_type: reactionType,
        });
      }
    } catch (err) {
      console.error("Erreur réaction :", err);
      loadInitialData();
    }
  };

  const openShareModal = (post) => {
    setSharingPost(post);
    setShareForm({ target: "feed", content: "", group_id: "" });
  };

  const handleShare = async (event) => {
    event.preventDefault();
    if (!sharingPost) return;

    try {
      const res = await apiClient.post(
        `/social/posts/${sharingPost.id}/share`,
        shareForm
      );

      setPosts((current) => [res.data.post, ...current]);
      setSharingPost(null);
      setShareForm({ target: "feed", content: "", group_id: "" });

      alert("Publication partagée !");
    } catch (err) {
      console.error("Erreur partage :", err);
      alert("Erreur lors du partage.");
    }
  };

  const toggleComments = async (postId) => {
    const nextOpen = !openComments[postId];

    setOpenComments((current) => ({
      ...current,
      [postId]: nextOpen,
    }));

    if (nextOpen && !commentsByPost[postId]) {
      const res = await apiClient.get(`/social/posts/${postId}/comments`);
      setCommentsByPost((current) => ({
        ...current,
        [postId]: res.data?.comments || [],
      }));
    }
  };

  const submitComment = async (postId) => {
    const content = (commentInputs[postId] || "").trim();
    if (!content) return;

    const res = await apiClient.post(`/social/posts/${postId}/comments`, {
      content,
    });

    setCommentsByPost((current) => ({
      ...current,
      [postId]: [...(current[postId] || []), res.data.comment],
    }));

    setCommentInputs((current) => ({
      ...current,
      [postId]: "",
    }));

    setPosts((current) =>
      current.map((post) =>
        post.id === postId
          ? {
              ...post,
              comment_count: Number(post.comment_count || 0) + 1,
            }
          : post
      )
    );
  };

  const deleteComment = async (postId, commentId) => {
    await apiClient.delete(`/social/comments/${commentId}`);

    setCommentsByPost((current) => ({
      ...current,
      [postId]: (current[postId] || []).filter(
        (comment) => comment.id !== commentId
      ),
    }));

    setPosts((current) =>
      current.map((post) =>
        post.id === postId
          ? {
              ...post,
              comment_count: Math.max(
                Number(post.comment_count || 0) - 1,
                0
              ),
            }
          : post
      )
    );
  };

  const submitGroup = async (event) => {
    event.preventDefault();
    if (!groupForm.name.trim()) return;

    const res = await apiClient.post("/social/groups", groupForm);

    setGroups((current) => [res.data.group, ...current]);
    setGroupForm({ name: "", description: "", privacy: "public" });
  };

  const toggleGroup = async (group) => {
    const res = group.has_joined
      ? await apiClient.delete(`/social/groups/${group.id}/leave`)
      : await apiClient.post(`/social/groups/${group.id}/join`);

    setGroups((current) =>
      current.map((item) => (item.id === group.id ? res.data.group : item))
    );
  };

  const handleHelpFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setHelpForm((current) => ({
      ...current,
      file,
      preview: URL.createObjectURL(file),
    }));
  };

  const submitHelp = async (event) => {
    event.preventDefault();

    try {
      setActionLoading(true);

      let imageUrl = editingAd?.image_url || null;

      if (helpForm.file) {
        console.log("UPLOAD HELP FILE:", helpForm.file);
        const formData = new FormData();
        formData.append("media", helpForm.file);
        console.log("FORMDATA ENTRIES:", [...formData.entries()]);

        const res = await apiClient.post("/social/upload", formData);
        imageUrl = res.data.media.url;
      }

      if (editingAd) {
        const res = await apiClient.patch(`/social/help/${editingAd.id}`, {
          ...helpForm,
          image_url: imageUrl,
        });

        setHelpItems((current) =>
          current.map((item) => (item.id === editingAd.id ? res.data.item : item))
        );
      } else {
        const res = await apiClient.post("/social/help", {
          ...helpForm,
          image_url: imageUrl,
        });

        setHelpItems((current) => [res.data.item, ...current]);
      }

      setIsHelpModalOpen(false);
      setEditingAd(null);
      setHelpForm({
        item_type: "demande",
        title: "",
        description: "",
        location: "",
        file: null,
        preview: "",
      });
    } catch (err) {
      console.error("Erreur help :", err);
      alert("Erreur entraide.");
    } finally {
      setActionLoading(false);
    }
  };

  const deleteHelp = async (id) => {
    if (!window.confirm("Supprimer ?")) return;

    await apiClient.delete(`/social/help/${id}`);
    setHelpItems((current) => current.filter((item) => item.id !== id));
  };

  const handleMarketFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setMarketForm((current) => ({
      ...current,
      file,
      preview: URL.createObjectURL(file),
    }));
  };

  const submitMarket = async (event) => {
    event.preventDefault();

    try {
      setActionLoading(true);

      let imageUrl = editingAd?.image_url || null;

      if (marketForm.file) {
        console.log("UPLOAD MARKET FILE:", marketForm.file);
        const formData = new FormData();
        formData.append("media", marketForm.file);
        console.log("FORMDATA ENTRIES:", [...formData.entries()]);

        const res = await apiClient.post("/social/upload", formData);
        imageUrl = res.data.media.url;
      }

      if (editingAd) {
        const res = await apiClient.patch(
          `/social/marketplace-sales/${editingAd.id}`,
          { ...marketForm, image_url: imageUrl }
        );

        setMarketplaceSales((current) =>
          current.map((item) => (item.id === editingAd.id ? res.data.item : item))
        );
      } else {
        const res = await apiClient.post("/social/marketplace-sales", {
          ...marketForm,
          image_url: imageUrl,
        });

        setMarketplaceSales((current) => [res.data.item, ...current]);
      }

      setIsMarketModalOpen(false);
      setEditingAd(null);
      setMarketForm({
        category: "autre",
        title: "",
        description: "",
        price: 0,
        location: "",
        item_condition: "bon_etat",
        file: null,
        preview: "",
      });
    } catch (err) {
      console.error("Erreur marketplace :", err);
      alert("Erreur marketplace.");
    } finally {
      setActionLoading(false);
    }
  };

  const deleteMarket = async (id) => {
    if (!window.confirm("Supprimer ?")) return;

    await apiClient.delete(`/social/marketplace-sales/${id}`);
    setMarketplaceSales((current) => current.filter((item) => item.id !== id));
  };

  const handleHousingFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setHousingForm((current) => ({
      ...current,
      file,
      preview: URL.createObjectURL(file),
    }));
  };

  const submitHousing = async (event) => {
    event.preventDefault();

    try {
      setActionLoading(true);

      let imageUrl = editingAd?.image_url || null;

      if (housingForm.file) {
        console.log("UPLOAD HOUSING FILE:", housingForm.file);
        const formData = new FormData();
        formData.append("media", housingForm.file);
        console.log("FORMDATA ENTRIES:", [...formData.entries()]);

        const res = await apiClient.post("/social/upload", formData);
        imageUrl = res.data.media.url;
      }

      const data = {
        ...housingForm,
        image_url: imageUrl,
      };

      delete data.file;
      delete data.preview;

      if (editingAd) {
        const res = await apiClient.patch(`/social/housing/${editingAd.id}`, data);

        setHousingListings((current) =>
          current.map((item) => (item.id === editingAd.id ? res.data.item : item))
        );
      } else {
        const res = await apiClient.post("/social/housing", data);

        setHousingListings((current) => [res.data.item, ...current]);
      }

      setIsHousingModalOpen(false);
      setEditingAd(null);
    } catch (err) {
      console.error("Erreur housing :", err);
      alert("Erreur logement.");
    } finally {
      setActionLoading(false);
    }
  };

  const deleteHousing = async (id) => {
    if (!window.confirm("Supprimer ?")) return;

    await apiClient.delete(`/social/housing/${id}`);
    setHousingListings((current) => current.filter((item) => item.id !== id));
  };

  const loadMessages = async (otherUserId) => {
    const res = await apiClient.get(`/social/messages/${otherUserId}`);

    setMessages(res.data?.messages || []);
    setSelectedConversation(otherUserId);
    setMessageForm((current) => ({
      ...current,
      receiver_id: otherUserId,
    }));
  };

  const submitMessage = async (event) => {
    event.preventDefault();

    if (!messageForm.content.trim() || !messageForm.receiver_id) return;

    const res = await apiClient.post("/social/messages", messageForm);

    setMessages((current) => [...current, res.data.message]);
    setMessageForm((current) => ({
      ...current,
      content: "",
    }));
  };

  const readNotification = async (notification) => {
    if (notification.read_at) return;

    await apiClient.patch(`/social/notifications/${notification.id}/read`);

    setNotifications((current) =>
      current.map((item) =>
        item.id === notification.id
          ? { ...item, read_at: new Date().toISOString() }
          : item
      )
    );
  };

  const navItems = [
    { id: "feed", label: "Fil d'actu", icon: Home },
    { id: "help", label: "Entraide", icon: Heart },
    { id: "marketplace", label: "Marketplace", icon: Briefcase },
    { id: "housing", label: "Logement", icon: Search },
    { id: "reels", label: "Reels", icon: Film },
    { id: "groups", label: "Groupes", icon: Users },
  ];

  return (
    <div className="social-page">
      <header className="social-topbar">
        <div className="social-topbar-left">
          <div className="social-logo-circle">N</div>

          <div className="social-search-box">
            <Search size={18} />
            <input
              placeholder="Rechercher..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>

        <div className="social-topbar-center">
          <button
            className={activeView === "feed" ? "active" : ""}
            onClick={() => setActiveView("feed")}
          >
            <Home size={22} />
          </button>

          <button
            className={activeView === "reels" ? "active" : ""}
            onClick={() => setActiveView("reels")}
          >
            <Film size={22} />
          </button>

          <button
            className={activeView === "groups" ? "active" : ""}
            onClick={() => setActiveView("groups")}
          >
            <Users size={22} />
          </button>
        </div>

        <div className="social-topbar-right">
          <button
            className={`topbar-icon-btn ${
              activeView === "messages" ? "active" : ""
            }`}
            onClick={() => setActiveView("messages")}
          >
            <MessageCircle size={20} />
            {conversations.some(
              (conversation) =>
                !conversation.is_read &&
                String(conversation.sender_id) !== String(user?.id)
            ) && <span className="icon-badge">!</span>}
          </button>

          <button
            className={`topbar-icon-btn ${
              activeView === "notifications" ? "active" : ""
            }`}
            onClick={() => setActiveView("notifications")}
          >
            <Bell size={20} />
            {unreadCount > 0 && <span className="icon-badge">{unreadCount}</span>}
          </button>

          <div className="social-user-pill">
            <AvatarCircle
              src={user?.avatar_url}
              name={displayName}
              size={32}
            />
            <span className="social-user-name">{displayName}</span>
          </div>
        </div>
      </header>

      <div className="social-layout">
        <aside className="social-column social-left">
          <nav className="social-card social-menu-card">
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                className={activeView === item.id ? "active" : ""}
                onClick={() => setActiveView(item.id)}
              >
                <item.icon size={20} />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>

          <section className="social-card">
            <h3>Mes Groupes</h3>
            {groups.filter((group) => group.has_joined).length === 0 && (
              <p style={{ color: "var(--social-text-muted)", fontSize: "0.85rem" }}>
                Aucun groupe rejoint.
              </p>
            )}
            {groups
              .filter((group) => group.has_joined)
              .map((group) => (
                <button
                  key={group.id}
                  type="button"
                  className="group-mini-row"
                  onClick={() => {
                    setActiveView("feed");
                    setSearch(group.name);
                  }}
                >
                  <div className="group-mini-icon">
                    <Users size={16} />
                  </div>
                  <div className="group-mini-info">
                    <strong>{group.name}</strong>
                    <span>{group.member_count} membres</span>
                  </div>
                </button>
              ))}
          </section>
        </aside>

        <main className="social-column social-center">
          {loading ? (
            <div className="social-loader" style={{ textAlign: "center", padding: "40px" }}>
              <Loader2 className="spin" size={40} />
            </div>
          ) : (
            <>
              {error && <div className="social-error">{error}</div>}

              {activeView === "feed" && (
                <>
                  <SocialReelsStrip
                    reels={stripReels}
                    onSelect={setSelectedReel}
                    onCreate={() => setIsReelCreatorOpen(true)}
                  />

                  <PostComposer
                    user={user}
                    displayName={displayName}
                    postForm={postForm}
                    setPostForm={setPostForm}
                    submitPost={submitPost}
                    handlePostFile={handlePostFile}
                    groups={groups}
                    actionLoading={actionLoading}
                  />

                  <div className="social-feed">
                    {filteredPosts.length === 0 ? (
                      <EmptyState title="Aucune publication trouvée." />
                    ) : (
                      filteredPosts.map((post) => (
                        <PostCard
                          key={post.id}
                          post={post}
                          currentUser={user}
                          comments={commentsByPost[post.id] || []}
                          commentsOpen={!!openComments[post.id]}
                          commentValue={commentInputs[post.id] || ""}
                          setCommentValue={(value) =>
                            setCommentInputs((current) => ({
                              ...current,
                              [post.id]: value,
                            }))
                          }
                          setReaction={setReaction}
                          openShareModal={openShareModal}
                          toggleComments={toggleComments}
                          submitComment={submitComment}
                          deleteComment={deleteComment}
                          deletePost={deletePost}
                        />
                      ))
                    )}
                  </div>
                </>
              )}

              {activeView === "reels" && (
                <ReelsView reels={reels} onSelect={setSelectedReel} />
              )}

              {activeView === "groups" && (
                <GroupsView
                  groups={groups}
                  groupForm={groupForm}
                  setGroupForm={setGroupForm}
                  submitGroup={submitGroup}
                  toggleGroup={toggleGroup}
                />
              )}

              {activeView === "help" && (
                <HelpView
                  items={filteredHelp}
                  filter={helpFilter}
                  setFilter={setHelpFilter}
                  search={globalAdSearch}
                  setSearch={setGlobalAdSearch}
                  onAddClick={() => {
                    setEditingAd(null);
                    setHelpForm({
                      item_type: "demande",
                      title: "",
                      description: "",
                      location: "",
                      file: null,
                      preview: "",
                    });
                    setIsHelpModalOpen(true);
                  }}
                  onEditItem={(item) => {
                    setEditingAd(item);
                    setHelpForm({
                      ...item,
                      preview: item.image_url ? mediaSrc(item.image_url) : "",
                    });
                    setIsHelpModalOpen(true);
                  }}
                  onDelete={deleteHelp}
                  onContact={handleContactOwner}
                  currentUser={user}
                />
              )}

              {activeView === "marketplace" && (
                <MarketplaceSalesView
                  items={filteredMarket}
                  filter={marketFilter}
                  setFilter={setMarketFilter}
                  search={globalAdSearch}
                  setSearch={setGlobalAdSearch}
                  onAddClick={() => {
                    setEditingAd(null);
                    setMarketForm({
                      category: "autre",
                      title: "",
                      description: "",
                      price: 0,
                      location: "",
                      item_condition: "bon_etat",
                      file: null,
                      preview: "",
                    });
                    setIsMarketModalOpen(true);
                  }}
                  onEditItem={(item) => {
                    setEditingAd(item);
                    setMarketForm({
                      ...item,
                      preview: item.image_url ? mediaSrc(item.image_url) : "",
                    });
                    setIsMarketModalOpen(true);
                  }}
                  onDelete={deleteMarket}
                  onContact={handleContactOwner}
                  currentUser={user}
                />
              )}

              {activeView === "housing" && (
                <HousingView
                  items={filteredHousing}
                  filter={housingFilter}
                  setFilter={setHousingFilter}
                  search={globalAdSearch}
                  setSearch={setGlobalAdSearch}
                  onAddClick={() => {
                    setEditingAd(null);
                    setHousingForm({
                      listing_type: "rental_offer",
                      housing_type: "appartement",
                      title: "",
                      description: "",
                      location: "",
                      surface_m2: "",
                      rooms: "",
                      rent_price: "",
                      charges: "",
                      deposit: "",
                      available_from: "",
                      current_location: "",
                      desired_location: "",
                      current_housing_type: "appartement",
                      desired_housing_type: "appartement",
                      current_surface_m2: "",
                      desired_surface_m2: "",
                      current_rooms: "",
                      desired_rooms: "",
                      exchange_reason: "",
                      file: null,
                      preview: "",
                    });
                    setIsHousingModalOpen(true);
                  }}
                  onEditItem={(item) => {
                    setEditingAd(item);
                    setHousingForm({
                      ...item,
                      preview: item.image_url ? mediaSrc(item.image_url) : "",
                    });
                    setIsHousingModalOpen(true);
                  }}
                  onDelete={deleteHousing}
                  onContact={handleContactOwner}
                  currentUser={user}
                />
              )}

              {activeView === "messages" && (
                <MessagesView
                  conversations={conversations}
                  selectedConversation={selectedConversation}
                  messages={messages}
                  currentUser={user}
                  messageForm={messageForm}
                  setMessageForm={setMessageForm}
                  loadMessages={loadMessages}
                  submitMessage={submitMessage}
                />
              )}

              {activeView === "notifications" && (
                <NotificationsView
                  notifications={notifications}
                  readNotification={readNotification}
                />
              )}
            </>
          )}
        </main>

        <aside className="social-column social-right">
          <section className="social-card">
            <h3>Annonces récentes</h3>
            
            {helpItems.length === 0 && marketplaceSales.length === 0 && (
              <p style={{ color: "var(--social-text-muted)", fontSize: "0.85rem" }}>
                Aucune annonce pour le moment.
              </p>
            )}

            {helpItems.slice(0, 2).map((item) => (
              <button
                key={item.id}
                className="ad-mini-row"
                onClick={() => setActiveView("help")}
              >
                {item.image_url ? (
                  <img src={mediaSrc(item.image_url)} alt="Aperçu" className="ad-mini-img" />
                ) : (
                  <div className="ad-mini-img" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Heart size={20} />
                  </div>
                )}
                <div className="ad-mini-info">
                  <strong>{item.title}</strong>
                  <span className="price">Entraide</span>
                  <span className="meta">{item.location || "N/A"}</span>
                </div>
              </button>
            ))}

            {marketplaceSales.slice(0, 2).map((item) => (
              <button
                key={item.id}
                className="ad-mini-row"
                onClick={() => setActiveView("marketplace")}
              >
                {item.image_url ? (
                  <img src={mediaSrc(item.image_url)} alt="Aperçu" className="ad-mini-img" />
                ) : (
                  <div className="ad-mini-img" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Briefcase size={20} />
                  </div>
                )}
                <div className="ad-mini-info">
                  <strong>{item.title}</strong>
                  <span className="price">{item.price} €</span>
                  <span className="meta">{item.location || "N/A"}</span>
                </div>
              </button>
            ))}
          </section>
        </aside>
      </div>

      {/* --- MODALS --- */}
      {isReelCreatorOpen && (
        <div
          className="social-modal-backdrop"
          onClick={() => setIsReelCreatorOpen(false)}
        >
          <div
            className="social-modal-card"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Créer un Reel</h3>
              <button
                className="close-btn"
                onClick={() => setIsReelCreatorOpen(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={submitReel} className="social-form">
              <textarea
                placeholder="Légende..."
                value={reelForm.content}
                onChange={(event) =>
                  setReelForm({
                    ...reelForm,
                    content: event.target.value,
                  })
                }
              />

              <div className="market-image-upload">
                <label className="social-tool-button">
                  <Film size={18} />
                  {reelForm.preview ? "Changer la vidéo" : "Sélectionner une vidéo"}
                  <input type="file" accept="video/*" onChange={handleReelFile} />
                </label>

                {reelForm.preview && (
                  <div className="market-preview-box">
                    <video src={reelForm.preview} controls style={{ width: '100%' }} />
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setIsReelCreatorOpen(false)}
                >
                  Annuler
                </button>

                <button
                  type="submit"
                  className="social-primary-btn"
                  disabled={actionLoading}
                >
                  {actionLoading ? <Loader2 className="spin" size={16} /> : <Plus size={16} />} Publier
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isHelpModalOpen && (
        <div
          className="social-modal-backdrop"
          onClick={() => setIsHelpModalOpen(false)}
        >
          <div
            className="social-modal-card"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <h3>{editingAd ? "Modifier" : "Nouveau"}</h3>
              <button
                className="close-btn"
                onClick={() => setIsHelpModalOpen(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={submitHelp} className="social-form">
              <select
                value={helpForm.item_type}
                onChange={(event) =>
                  setHelpForm({
                    ...helpForm,
                    item_type: event.target.value,
                  })
                }
                required
              >
                {HELP_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>

              <input
                placeholder="Titre"
                value={helpForm.title}
                onChange={(event) =>
                  setHelpForm({
                    ...helpForm,
                    title: event.target.value,
                  })
                }
                required
              />

              <input
                placeholder="Localisation"
                value={helpForm.location}
                onChange={(event) =>
                  setHelpForm({
                    ...helpForm,
                    location: event.target.value,
                  })
                }
              />

              <textarea
                placeholder="Description"
                value={helpForm.description}
                onChange={(event) =>
                  setHelpForm({
                    ...helpForm,
                    description: event.target.value,
                  })
                }
                required
              />

              <div className="market-image-upload">
                <label className="social-tool-button">
                  <Image size={18} />
                  Image
                  <input type="file" accept="image/*" onChange={handleHelpFile} />
                </label>

                {helpForm.preview && (
                  <div className="market-preview-box">
                    <img src={helpForm.preview} alt="Aperçu" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setIsHelpModalOpen(false)}>
                  Annuler
                </button>

                <button
                  type="submit"
                  className="social-primary-btn"
                  disabled={actionLoading}
                >
                  {actionLoading ? <Loader2 className="spin" size={16} /> : <Plus size={16} />} Publier
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isMarketModalOpen && (
        <div
          className="social-modal-backdrop"
          onClick={() => setIsMarketModalOpen(false)}
        >
          <div
            className="social-modal-card"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Vendre un objet</h3>
              <button
                className="close-btn"
                onClick={() => setIsMarketModalOpen(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={submitMarket} className="social-form">
              <select
                value={marketForm.category}
                onChange={(event) =>
                  setMarketForm({
                    ...marketForm,
                    category: event.target.value,
                  })
                }
                required
              >
                {MARKET_CATEGORIES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>

              <input
                placeholder="Titre"
                value={marketForm.title}
                onChange={(event) =>
                  setMarketForm({
                    ...marketForm,
                    title: event.target.value,
                  })
                }
                required
              />

              <div className="row-flex">
                <input
                  type="number"
                  placeholder="Prix (€)"
                  value={marketForm.price}
                  onChange={(event) =>
                    setMarketForm({
                      ...marketForm,
                      price: event.target.value,
                    })
                  }
                  required
                />
                <select
                  value={marketForm.item_condition}
                  onChange={(event) =>
                    setMarketForm({
                      ...marketForm,
                      item_condition: event.target.value,
                    })
                  }
                >
                  <option value="neuf">Neuf</option>
                  <option value="tres_bon_etat">Très bon état</option>
                  <option value="bon_etat">Bon état</option>
                  <option value="etat_moyen">État moyen</option>
                  <option value="a_reparer">À réparer</option>
                </select>
              </div>

              <input
                placeholder="Localisation"
                value={marketForm.location}
                onChange={(event) =>
                  setMarketForm({
                    ...marketForm,
                    location: event.target.value,
                  })
                }
              />

              <textarea
                placeholder="Description"
                value={marketForm.description}
                onChange={(event) =>
                  setMarketForm({
                    ...marketForm,
                    description: event.target.value,
                  })
                }
                required
              />

              <div className="market-image-upload">
                <label className="social-tool-button">
                  <Image size={18} />
                  Image
                  <input type="file" accept="image/*" onChange={handleMarketFile} />
                </label>

                {marketForm.preview && (
                  <div className="market-preview-box">
                    <img src={marketForm.preview} alt="Aperçu" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setIsMarketModalOpen(false)}>
                  Annuler
                </button>

                <button
                  type="submit"
                  className="social-primary-btn"
                  disabled={actionLoading}
                >
                  {actionLoading ? <Loader2 className="spin" size={16} /> : <Plus size={16} />} Vendre
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isHousingModalOpen && (
        <div
          className="social-modal-backdrop"
          onClick={() => setIsHousingModalOpen(false)}
        >
          <div
            className="social-modal-card large"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Logement</h3>
              <button
                className="close-btn"
                onClick={() => setIsHousingModalOpen(false)}
              >
                ×
              </button>
            </div>

            <form onSubmit={submitHousing} className="social-form">
              <div className="row-flex">
                <select
                  value={housingForm.listing_type}
                  onChange={(event) =>
                    setHousingForm({
                      ...housingForm,
                      listing_type: event.target.value,
                    })
                  }
                  required
                >
                  {HOUSING_LISTING_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>

                <select
                  value={housingForm.housing_type}
                  onChange={(event) =>
                    setHousingForm({
                      ...housingForm,
                      housing_type: event.target.value,
                    })
                  }
                  required
                >
                  {HOUSING_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <input
                placeholder="Titre"
                value={housingForm.title}
                onChange={(event) =>
                  setHousingForm({
                    ...housingForm,
                    title: event.target.value,
                  })
                }
                required
              />

              {housingForm.listing_type !== "housing_exchange" ? (
                <>
                  <div className="row-flex">
                    <input
                      placeholder="Ville / Quartier"
                      value={housingForm.location}
                      onChange={(event) =>
                        setHousingForm({
                          ...housingForm,
                          location: event.target.value,
                        })
                      }
                      required
                    />
                    <input
                      type="number"
                      placeholder="Surface (m²)"
                      value={housingForm.surface_m2}
                      onChange={(event) =>
                        setHousingForm({
                          ...housingForm,
                          surface_m2: event.target.value,
                        })
                      }
                    />
                    <input
                      type="number"
                      placeholder="Pièces"
                      value={housingForm.rooms}
                      onChange={(event) =>
                        setHousingForm({
                          ...housingForm,
                          rooms: event.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="row-flex">
                    <input
                      type="number"
                      placeholder="Loyer / Prix (€)"
                      value={housingForm.rent_price}
                      onChange={(event) =>
                        setHousingForm({
                          ...housingForm,
                          rent_price: event.target.value,
                        })
                      }
                    />
                    <input
                      type="number"
                      placeholder="Charges (€)"
                      value={housingForm.charges}
                      onChange={(event) =>
                        setHousingForm({
                          ...housingForm,
                          charges: event.target.value,
                        })
                      }
                    />
                    <input
                      type="date"
                      value={housingForm.available_from}
                      onChange={(event) =>
                        setHousingForm({
                          ...housingForm,
                          available_from: event.target.value,
                        })
                      }
                    />
                  </div>
                </>
              ) : (
                <div className="exchange-fields">
                  <div className="row-flex">
                    <input
                      placeholder="Localisation actuelle"
                      value={housingForm.current_location}
                      onChange={(event) =>
                        setHousingForm({
                          ...housingForm,
                          current_location: event.target.value,
                        })
                      }
                    />
                    <input
                      placeholder="Localisation souhaitée"
                      value={housingForm.desired_location}
                      onChange={(event) =>
                        setHousingForm({
                          ...housingForm,
                          desired_location: event.target.value,
                        })
                      }
                    />
                  </div>
                  <textarea
                    placeholder="Raison de l'échange"
                    value={housingForm.exchange_reason}
                    onChange={(event) =>
                      setHousingForm({
                        ...housingForm,
                        exchange_reason: event.target.value,
                      })
                    }
                  />
                </div>
              )}

              <textarea
                placeholder="Description"
                value={housingForm.description}
                onChange={(event) =>
                  setHousingForm({
                    ...housingForm,
                    description: event.target.value,
                  })
                }
                required
              />

              <div className="market-image-upload">
                <label className="social-tool-button">
                  <Image size={18} />
                  Image
                  <input type="file" accept="image/*" onChange={handleHousingFile} />
                </label>

                {housingForm.preview && (
                  <div className="market-preview-box">
                    <img src={housingForm.preview} alt="Aperçu" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                )}
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  onClick={() => setIsHousingModalOpen(false)}
                >
                  Annuler
                </button>

                <button
                  type="submit"
                  className="social-primary-btn"
                  disabled={actionLoading}
                >
                  {actionLoading ? <Loader2 className="spin" size={16} /> : <Plus size={16} />} Publier
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedReel && (
        <ReelModal
          reel={selectedReel}
          onClose={() => setSelectedReel(null)}
          currentUser={user}
          comments={commentsByPost[selectedReel.id] || []}
          commentsOpen={true}
          commentValue={commentInputs[selectedReel.id] || ""}
          setCommentValue={(val) =>
            setCommentInputs((p) => ({ ...p, [selectedReel.id]: val }))
          }
          setReaction={setReaction}
          toggleComments={toggleComments}
          submitComment={submitComment}
          deleteComment={deleteComment}
        />
      )}

      {sharingPost && (
        <div
          className="social-modal-backdrop"
          onClick={() => setSharingPost(null)}
        >
          <div
            className="social-modal-card"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Partager la publication</h3>
              <button
                className="close-btn"
                onClick={() => setSharingPost(null)}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleShare} className="social-form">
              <select
                value={shareForm.target}
                onChange={(event) =>
                  setShareForm({
                    ...shareForm,
                    target: event.target.value,
                  })
                }
              >
                <option value="feed">Mon fil</option>
                <option value="group">Un groupe</option>
              </select>

              {shareForm.target === "group" && (
                <select
                  value={shareForm.group_id}
                  onChange={(event) =>
                    setShareForm({
                      ...shareForm,
                      group_id: event.target.value,
                    })
                  }
                  required
                >
                  <option value="">Choisir un groupe...</option>
                  {groups.map((group) => (
                    <option key={group.id} value={group.id}>
                      {group.name}
                    </option>
                  ))}
                </select>
              )}

              <textarea
                placeholder="Ajouter un message..."
                value={shareForm.content}
                onChange={(event) =>
                  setShareForm({
                    ...shareForm,
                    content: event.target.value,
                  })
                }
              />

              <div className="modal-actions">
                <button type="button" onClick={() => setSharingPost(null)}>
                  Annuler
                </button>

                <button type="submit" className="social-primary-btn">
                  Partager
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <nav className="social-mobile-nav">
        {navItems.map((item) => (
          <button
            key={item.id}
            type="button"
            className={activeView === item.id ? "active" : ""}
            onClick={() => {
              setActiveView(item.id);
              window.scrollTo(0, 0);
            }}
          >
            <item.icon size={20} />
          </button>
        ))}
      </nav>
    </div>
  );
}

function SocialReelsStrip({ reels, onSelect, onCreate }) {
  return (
    <section className="social-reels-strip">
      <div className="social-reels-list">
        <div
          className="social-reel-card social-reel-add-card"
          onClick={onCreate}
        >
          <div className="social-reel-plus">
            <Plus size={28} />
          </div>
          <span className="social-reel-author">Créer un reel</span>
        </div>

        {reels.map((reel) => {
          const video = reel.media?.find((m) => m.media_type === "video");
          const author = getAuthorName(reel);
          const avatarUrl = getAvatarUrl(reel);
          
          return (
            <div
              className="social-reel-card"
              key={reel.id}
              onClick={() => onSelect(reel)}
            >
              {video && (
                <video
                  src={mediaSrc(video.url)}
                  preload="metadata"
                  className="social-reel-video"
                />
              )}
              <div className="social-reel-overlay">
                <div className="social-reel-play">
                  <Film size={22} />
                </div>
                <div className="social-reel-stats-mini">
                  <Heart size={10} fill="white" /> {reel.reaction_count || 0}
                </div>
                <div className="social-reel-author-box">
                  <AvatarCircle src={avatarUrl} name={author} size={24} />
                  <span className="social-reel-author">{author}</span>
                </div>
              </div>
            </div>
          );
        })}

        {reels.length === 0 && (
          <div className="empty-reels-hint">
            <p style={{ color: "var(--social-text-muted)" }}>Aucun reel publié.</p>
          </div>
        )}
      </div>
    </section>
  );
}

function PostComposer({
  user,
  displayName,
  postForm,
  setPostForm,
  submitPost,
  handlePostFile,
  groups,
  actionLoading,
}) {
  return (
    <section className="social-card social-composer">
      <div className="composer-header">
        <AvatarCircle src={user?.avatar_url} name={displayName} size={48} />
        
        <select
          className="composer-select"
          value={postForm.visibility}
          onChange={(event) =>
            setPostForm({ ...postForm, visibility: event.target.value })
          }
        >
          <option value="public">Public</option>
          <option value="friends">Amis seulement</option>
        </select>

        {groups.length > 0 && (
          <select
            className="composer-select"
            value={postForm.group_id}
            onChange={(event) =>
              setPostForm({ ...postForm, group_id: event.target.value })
            }
          >
            <option value="">Sur mon fil</option>
            {groups
              .filter((group) => group.has_joined)
              .map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
          </select>
        )}
      </div>

      <form onSubmit={submitPost}>
        <textarea
          placeholder="Que voulez-vous dire ?"
          value={postForm.content}
          onChange={(event) =>
            setPostForm({ ...postForm, content: event.target.value })
          }
        />

        {postForm.preview && (
          <div className="composer-preview">
            {postForm.post_type === "reel" ? (
              <video src={postForm.preview} controls />
            ) : (
              <img src={postForm.preview} alt="Aperçu" />
            )}
            <button
              type="button"
              onClick={() =>
                setPostForm({
                  ...postForm,
                  file: null,
                  preview: "",
                  post_type: "post",
                })
              }
            >
              ×
            </button>
          </div>
        )}

        <div className="composer-toolbar">
          <label className="social-tool-button image">
            <Image size={20} />
            Image
            <input type="file" accept="image/*" onChange={handlePostFile} />
          </label>
          <label className="social-tool-button video">
            <Film size={20} />
            Vidéo
            <input type="file" accept="video/*" onChange={handlePostFile} />
          </label>

          <button
            type="submit"
            className="social-primary-btn"
            disabled={actionLoading}
          >
            {actionLoading ? <Loader2 className="spin" size={18} /> : <Plus size={18} />}
            Publier
          </button>
        </div>
      </form>
    </section>
  );
}

function PostCard({
  post,
  currentUser,
  comments,
  commentsOpen,
  commentValue,
  setCommentValue,
  setReaction,
  openShareModal,
  toggleComments,
  submitComment,
  deleteComment,
  deletePost,
  isSharedView = false,
}) {
  const authorName = getAuthorName(post);
  const avatarUrl = getAvatarUrl(post);
  const myReaction =
    REACTIONS.find((r) => r.id === post.my_reaction) || REACTIONS[0];

  const canDelete =
    !isSharedView &&
    (String(post.user_id) === String(currentUser?.id) ||
      String(currentUser?.role || "").toLowerCase().includes("admin"));

  const topReactions = post.reaction_summary
    ? Object.entries(post.reaction_summary)
        .filter(([_, count]) => count > 0)
        .sort(([_, a], [__, b]) => b - a)
        .slice(0, 3)
        .map(([id]) => REACTIONS.find((r) => r.id === id)?.emoji)
    : [];

  return (
    <article
      className={`social-card post-card ${isSharedView ? "is-shared" : ""}`}
    >
      <header className="post-header">
        <AvatarCircle src={avatarUrl} name={authorName} size={42} />

        <div className="post-header-info">
          <strong>
            {authorName}
            <span className="visibility-badge">
              {post.visibility === "friends" ? (
                <UserCheck size={12} />
              ) : (
                <Eye size={12} />
              )}
              {post.visibility}
            </span>
          </strong>
          <span>
            {formatDate(post.created_at)}
            {post.group_name ? ` · ${post.group_name}` : ""}
          </span>
        </div>

        {canDelete && (
          <button
            type="button"
            className="icon-danger"
            onClick={() => deletePost(post.id)}
          >
            <Trash2 size={18} />
          </button>
        )}
      </header>

      {post.content && <div className="post-content">{post.content}</div>}

      {post.shared_post && (
        <div className="shared-content" style={{ borderLeft: "3px solid var(--social-primary)", paddingLeft: "16px", margin: "12px 0", background: "rgba(255,255,255,0.02)", padding: "16px", borderRadius: "12px" }}>
          <PostCard
            post={post.shared_post}
            currentUser={currentUser}
            isSharedView={true}
          />
        </div>
      )}

      {post.media?.length > 0 && (
        <div className="post-media-grid">
          {post.media.map((media) =>
            media.media_type === "video" ? (
              <video key={media.id} src={mediaSrc(media.url)} controls />
            ) : (
              <img
                key={media.id}
                src={mediaSrc(media.url)}
                alt="Média publication"
              />
            )
          )}
        </div>
      )}

      {!isSharedView && (
        <>
          <div className="post-stats">
            <div className="reactions-pill">
              {topReactions.length > 0 && (
                <div className="top-emojis">
                  {topReactions.map((emoji, index) => (
                    <span key={index}>{emoji}</span>
                  ))}
                </div>
              )}
              {post.reaction_count > 0 && <span>{post.reaction_count}</span>}
            </div>

            <div className="stats-right">
              <span onClick={() => toggleComments(post.id)}>
                {post.comment_count || 0} commentaires
              </span>
              <span>{post.share_count || 0} partages</span>
            </div>
          </div>

          <div className="post-actions">
            <div className="reaction-wrapper">
              <button
                type="button"
                className={post.my_reaction ? "active" : ""}
                onClick={() => setReaction(post, post.my_reaction || "like")}
                style={{
                  color: post.my_reaction ? myReaction.color : "inherit",
                }}
              >
                <span className="reaction-emoji">
                  {post.my_reaction ? myReaction.emoji : "👍"}
                </span>
                {post.my_reaction ? myReaction.label : "J'aime"}
              </button>

              <div className="reaction-picker">
                {REACTIONS.map((reaction) => (
                  <button
                    key={reaction.id}
                    type="button"
                    onClick={() => setReaction(post, reaction.id)}
                    title={reaction.label}
                  >
                    {reaction.emoji}
                  </button>
                ))}
              </div>
            </div>

            <button type="button" onClick={() => toggleComments(post.id)}>
              <MessageCircle size={18} />
              Commenter
            </button>

            <button type="button" onClick={() => openShareModal(post)}>
              <Share2 size={18} />
              Partager
            </button>
          </div>

          {commentsOpen && (
            <div className="comment-section">
              {comments.map((comment) => {
                const commentAuthorName = getAuthorName(comment);
                const commentAvatarUrl = getAvatarUrl(comment);
                const canDeleteComment =
                  String(comment.user_id) === String(currentUser?.id) ||
                  String(currentUser?.role || "")
                    .toLowerCase()
                    .includes("admin");

                return (
                  <div className="comment-item" key={comment.id}>
                    <AvatarCircle src={commentAvatarUrl} name={commentAuthorName} size={32} />

                    <div className="comment-body">
                      <strong>{commentAuthorName}</strong>
                      <p>{comment.content}</p>
                      <span>{formatDate(comment.created_at)}</span>
                    </div>

                    {canDeleteComment && (
                      <button
                        type="button"
                        className="icon-danger"
                        onClick={() => deleteComment(post.id, comment.id)}
                        style={{ alignSelf: "flex-start" }}
                      >
                        <Trash2 size={16} />
                      </button>
                    )}
                  </div>
                );
              })}

              <div className="comment-form">
                <input
                  value={commentValue}
                  onChange={(event) => setCommentValue(event.target.value)}
                  placeholder="Écrire un commentaire..."
                />
                <button type="button" onClick={() => submitComment(post.id)}>
                  <Send size={18} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </article>
  );
}

function ReelsView({ reels, onSelect }) {
  return (
    <section className="social-panel">
      <div className="section-heading">
        <h2>Reels</h2>
        <p>Toutes les vidéos courtes du réseau.</p>
      </div>

      {reels.length === 0 ? (
        <EmptyState title="Aucun Reel publié pour l'instant." />
      ) : (
        <div className="reels-grid">
          {reels.map((reel) => {
            const video = reel.media?.find((m) => m.media_type === "video");
            const authorName = getAuthorName(reel);
            const avatarUrl = getAvatarUrl(reel);
            return (
              <article
                className="reel-card"
                key={reel.id}
                onClick={() => onSelect(reel)}
                style={{ cursor: "pointer", position: "relative", aspectRatio: "9/16", background: "black", borderRadius: "12px", overflow: "hidden" }}
              >
                {video && (
                  <video
                    src={mediaSrc(video.url)}
                    style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.8 }}
                  />
                )}
                <div style={{ position: "absolute", inset: 0, padding: "10px", display: "flex", flexDirection: "column", justifyContent: "space-between", background: "linear-gradient(to bottom, transparent 60%, rgba(0,0,0,0.8))" }}>
                   <div style={{ margin: "auto", color: "white" }}><Film size={24} /></div>
                   <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                     <AvatarCircle src={avatarUrl} name={authorName} size={24} />
                     <strong style={{ color: "white", fontSize: "0.8rem", textShadow: "0 1px 4px rgba(0,0,0,0.8)" }}>{authorName}</strong>
                   </div>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}

function GroupsView({
  groups,
  groupForm,
  setGroupForm,
  submitGroup,
  toggleGroup,
}) {
  return (
    <section className="social-panel">
      <div className="section-heading">
        <h2>Groupes</h2>
        <p>Espaces de discussion thématiques.</p>
      </div>

      <form className="social-form" onSubmit={submitGroup}>
        <input
          placeholder="Nom du groupe"
          value={groupForm.name}
          onChange={(event) =>
            setGroupForm({ ...groupForm, name: event.target.value })
          }
          required
        />
        <textarea
          placeholder="Description"
          value={groupForm.description}
          onChange={(event) =>
            setGroupForm({ ...groupForm, description: event.target.value })
          }
        />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button type="submit" className="social-primary-btn">
            Créer le groupe
          </button>
        </div>
      </form>

      {groups.length === 0 ? (
        <EmptyState title="Aucun groupe n'existe pour le moment." />
      ) : (
        <div className="social-list-grid">
          {groups.map((group) => (
            <article className="social-card" key={group.id} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ width: "48px", height: "48px", background: "rgba(255,255,255,0.05)", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Users size={24} color="var(--social-primary)" />
                </div>
                <div>
                  <h3 style={{ fontSize: "1.1rem", margin: 0 }}>{group.name}</h3>
                  <span style={{ fontSize: "0.85rem", color: "var(--social-text-muted)" }}>{group.member_count} membres</span>
                </div>
              </div>
              <p style={{ fontSize: "0.9rem", color: "#d1d5db" }}>{group.description}</p>
              
              <button
                type="button"
                className={group.has_joined ? "" : "social-primary-btn"}
                onClick={() => toggleGroup(group)}
                style={{
                  marginTop: "auto",
                  background: group.has_joined ? "rgba(255,255,255,0.1)" : "var(--social-primary)",
                  border: "none",
                  color: "white",
                  padding: "8px",
                  borderRadius: "8px",
                  cursor: "pointer"
                }}
              >
                {group.has_joined ? "Quitter le groupe" : "Rejoindre"}
              </button>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function HelpView({
  items,
  filter,
  setFilter,
  search,
  setSearch,
  onAddClick,
  onEditItem,
  onDelete,
  onContact,
  currentUser,
}) {
  return (
    <section className="social-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div>
          <h2 style={{ fontSize: "1.5rem", margin: 0 }}>Entraide</h2>
          <p style={{ color: "var(--social-text-muted)", fontSize: "0.9rem", margin: "4px 0 0 0" }}>Dons, services et coups de main entre membres.</p>
        </div>
        <button className="social-primary-btn" onClick={onAddClick}>
          <Plus size={18} /> Publier
        </button>
      </div>

      <div className="marketplace-controls">
        <div className="social-search-box" style={{ width: "100%", marginBottom: "12px" }}>
          <Search size={18} />
          <input
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="market-filters">
          <button
            className={filter === "all" ? "active" : ""}
            onClick={() => setFilter("all")}
          >
            Toutes
          </button>
          {HELP_TYPES.map((t) => (
            <button
              key={t.value}
              className={filter === t.value ? "active" : ""}
              onClick={() => setFilter(t.value)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="social-list-grid">
        {items.map((item) => {
          const authorName = getAuthorName(item);
          const avatarUrl = getAvatarUrl(item);

          return (
            <article className="social-card market-card" key={item.id}>
              {item.image_url && (
                <div className="market-card-image">
                  <img src={mediaSrc(item.image_url)} alt={item.title} />
                </div>
              )}
              <div className="market-card-body">
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "8px" }}>
                  <span style={{ fontSize: "11px", background: "rgba(255,255,255,0.1)", padding: "2px 8px", borderRadius: "4px" }}>
                    {HELP_TYPES.find((t) => t.value === item.item_type)?.label}
                  </span>
                </div>
                
                <h3 style={{ fontSize: "1.1rem", margin: "0 0 8px 0" }}>{item.title}</h3>
                <p className="market-desc">{item.description}</p>
                
                <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: "8px", paddingTop: "12px", borderTop: "1px solid var(--social-border)" }}>
                  <AvatarCircle src={avatarUrl} name={authorName} size={28} />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <strong style={{ fontSize: "0.85rem" }}>{authorName}</strong>
                    <span style={{ fontSize: "0.75rem", color: "var(--social-text-muted)" }}>{item.location || "Localisation non précisée"}</span>
                  </div>
                </div>

                <div className="market-actions">
                  {item.user_id !== currentUser?.id && (
                    <button
                      className="social-primary-btn"
                      onClick={() => onContact(item)}
                      style={{ width: "100%", justifyContent: "center" }}
                    >
                      <MessageCircle size={16} /> Contacter
                    </button>
                  )}
                  {(item.user_id === currentUser?.id ||
                    currentUser?.role === "admin") && (
                    <div className="owner-actions">
                      <button onClick={() => onEditItem(item)}>Modifier</button>
                      <button
                        className="danger"
                        onClick={() => onDelete(item.id)}
                      >
                        Supprimer
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function MarketplaceSalesView({
  items,
  filter,
  setFilter,
  search,
  setSearch,
  onAddClick,
  onEditItem,
  onDelete,
  onContact,
  currentUser,
}) {
  return (
    <section className="social-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div>
          <h2 style={{ fontSize: "1.5rem", margin: 0 }}>Marketplace</h2>
          <p style={{ color: "var(--social-text-muted)", fontSize: "0.9rem", margin: "4px 0 0 0" }}>Vente d'objets entre membres.</p>
        </div>
        <button className="social-primary-btn" onClick={onAddClick}>
          <Plus size={18} /> Vendre
        </button>
      </div>

      <div className="marketplace-controls">
        <div className="social-search-box" style={{ width: "100%", marginBottom: "12px" }}>
          <Search size={18} />
          <input
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="market-filters">
          <button
            className={filter === "all" ? "active" : ""}
            onClick={() => setFilter("all")}
          >
            Toutes
          </button>
          {MARKET_CATEGORIES.map((t) => (
            <button
              key={t.value}
              className={filter === t.value ? "active" : ""}
              onClick={() => setFilter(t.value)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="social-list-grid">
        {items.map((item) => {
          const authorName = getAuthorName(item);
          const avatarUrl = getAvatarUrl(item);

          return (
            <article className="social-card market-card" key={item.id}>
              {item.image_url && (
                <div className="market-card-image">
                  <img src={mediaSrc(item.image_url)} alt={item.title} />
                </div>
              )}
              <div className="market-card-body">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
                  <span style={{ fontSize: "1.2rem", fontWeight: "700", color: "var(--social-success)" }}>
                    {item.price} €
                  </span>
                  <span style={{ fontSize: "11px", background: "rgba(255,255,255,0.1)", padding: "2px 8px", borderRadius: "4px" }}>
                    {MARKET_CATEGORIES.find((t) => t.value === item.category)?.label}
                  </span>
                </div>

                <h3 style={{ fontSize: "1.1rem", margin: "0 0 8px 0" }}>{item.title}</h3>
                <p className="market-desc">{item.description}</p>
                
                <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: "8px", paddingTop: "12px", borderTop: "1px solid var(--social-border)" }}>
                  <AvatarCircle src={avatarUrl} name={authorName} size={28} />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <strong style={{ fontSize: "0.85rem" }}>{authorName}</strong>
                    <span style={{ fontSize: "0.75rem", color: "var(--social-text-muted)" }}>{item.location || "N/A"}</span>
                  </div>
                </div>

                <div className="market-actions">
                  {item.user_id !== currentUser?.id && (
                    <button
                      className="social-primary-btn"
                      onClick={() => onContact(item)}
                      style={{ width: "100%", justifyContent: "center" }}
                    >
                      <MessageCircle size={16} /> Contacter
                    </button>
                  )}
                  {(item.user_id === currentUser?.id ||
                    currentUser?.role === "admin") && (
                    <div className="owner-actions">
                      <button onClick={() => onEditItem(item)}>Modifier</button>
                      <button
                        className="danger"
                        onClick={() => onDelete(item.id)}
                      >
                        Supprimer
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function HousingView({
  items,
  filter,
  setFilter,
  search,
  setSearch,
  onAddClick,
  onEditItem,
  onDelete,
  onContact,
  currentUser,
}) {
  return (
    <section className="social-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
        <div>
          <h2 style={{ fontSize: "1.5rem", margin: 0 }}>Logement</h2>
          <p style={{ color: "var(--social-text-muted)", fontSize: "0.9rem", margin: "4px 0 0 0" }}>Locations, recherches et échanges.</p>
        </div>
        <button className="social-primary-btn" onClick={onAddClick}>
          <Plus size={18} /> Publier
        </button>
      </div>

      <div className="marketplace-controls">
        <div className="social-search-box" style={{ width: "100%", marginBottom: "12px" }}>
          <Search size={18} />
          <input
            placeholder="Rechercher..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="market-filters">
          <button
            className={filter === "all" ? "active" : ""}
            onClick={() => setFilter("all")}
          >
            Toutes
          </button>
          {HOUSING_LISTING_TYPES.map((t) => (
            <button
              key={t.value}
              className={filter === t.value ? "active" : ""}
              onClick={() => setFilter(t.value)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="social-list-grid">
        {items.map((item) => {
          const authorName = getAuthorName(item);
          const avatarUrl = getAvatarUrl(item);

          return (
            <article className="social-card market-card housing-card" key={item.id}>
              {item.image_url && (
                <div className="market-card-image">
                  <img src={mediaSrc(item.image_url)} alt={item.title} />
                </div>
              )}
              <div className="market-card-body">
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "12px" }}>
                  <span style={{ fontSize: "11px", background: "rgba(255,255,255,0.1)", padding: "2px 8px", borderRadius: "4px" }}>
                    {HOUSING_LISTING_TYPES.find((t) => t.value === item.listing_type)?.label}
                  </span>
                </div>
                
                <h3 style={{ fontSize: "1.1rem", margin: "0 0 8px 0" }}>{item.title}</h3>
                
                {item.listing_type !== "housing_exchange" ? (
                  <div style={{ background: "rgba(255,255,255,0.03)", padding: "10px", borderRadius: "8px", marginBottom: "12px" }}>
                    <strong style={{ color: "var(--social-success)", fontSize: "1.1rem" }}>{item.rent_price} €</strong> / mois
                    <div style={{ fontSize: "0.85rem", color: "var(--social-text-muted)", marginTop: "4px" }}>
                      {item.surface_m2}m² · {item.rooms} pièces
                    </div>
                  </div>
                ) : (
                  <div style={{ background: "rgba(37,99,235,0.1)", padding: "10px", borderRadius: "8px", marginBottom: "12px", fontSize: "0.9rem" }}>
                    🔁 {item.current_housing_type} ({item.current_location}) ➔ {item.desired_housing_type} ({item.desired_location})
                  </div>
                )}
                
                <p className="market-desc">{item.description}</p>

                <div style={{ marginTop: "auto", display: "flex", alignItems: "center", gap: "8px", paddingTop: "12px", borderTop: "1px solid var(--social-border)" }}>
                  <AvatarCircle src={avatarUrl} name={authorName} size={28} />
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    <strong style={{ fontSize: "0.85rem" }}>{authorName}</strong>
                    <span style={{ fontSize: "0.75rem", color: "var(--social-text-muted)" }}>{item.location || "N/A"}</span>
                  </div>
                </div>

                <div className="market-actions">
                  {item.user_id !== currentUser?.id && (
                    <button
                      className="social-primary-btn"
                      onClick={() => onContact(item)}
                      style={{ width: "100%", justifyContent: "center" }}
                    >
                      <MessageCircle size={16} /> Contacter
                    </button>
                  )}
                  {(item.user_id === currentUser?.id ||
                    currentUser?.role === "admin") && (
                    <div className="owner-actions">
                      <button onClick={() => onEditItem(item)}>Modifier</button>
                      <button
                        className="danger"
                        onClick={() => onDelete(item.id)}
                      >
                        Supprimer
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function MessagesView({
  conversations,
  selectedConversation,
  messages,
  currentUser,
  messageForm,
  setMessageForm,
  loadMessages,
  submitMessage,
}) {
  return (
    <section className="social-panel">
      <div className="section-heading">
        <h2>Messages</h2>
        <p>Discutez en privé avec les autres membres.</p>
      </div>
      <div className="messages-layout">
        <div className="conversation-list">
          {conversations.length === 0 && (
             <div style={{ padding: "20px", color: "var(--social-text-muted)", fontSize: "0.9rem" }}>Aucune conversation.</div>
          )}
          {conversations.map((c) => {
            const isActive = String(selectedConversation) === String(c.other_user_id);
            return (
              <button
                key={c.other_user_id}
                className={isActive ? "active" : ""}
                onClick={() => loadMessages(c.other_user_id)}
                style={{ display: "flex", alignItems: "center", gap: "12px", padding: "16px" }}
              >
                <AvatarCircle src={c.other_avatar || ""} name={c.other_name} size={40} />
                <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", overflow: "hidden", flex: 1 }}>
                  <strong style={{ fontSize: "0.95rem", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "100%" }}>{c.other_name}</strong>
                  <span style={{ fontSize: "0.8rem", color: "var(--social-text-muted)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "100%" }}>
                    {c.content}
                  </span>
                </div>
                {!c.is_read && String(c.sender_id) !== String(currentUser?.id) && (
                  <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: "var(--social-primary)", flexShrink: 0 }}></div>
                )}
              </button>
            );
          })}
        </div>
        <div className="message-thread">
          <div className="message-list">
            {messages.length === 0 && (
              <div style={{ margin: "auto", color: "var(--social-text-muted)" }}>Sélectionnez une conversation.</div>
            )}
            {messages.map((m) => (
              <div
                key={m.id}
                className={`message-bubble ${
                  String(m.sender_id) === String(currentUser?.id) ? "mine" : ""
                }`}
              >
                {m.content}
              </div>
            ))}
          </div>
          {selectedConversation && (
            <form className="message-form" onSubmit={submitMessage}>
              <input
                value={messageForm.content}
                onChange={(e) =>
                  setMessageForm({ ...messageForm, content: e.target.value })
                }
                placeholder="Écrire un message..."
              />
              <button type="submit" style={{ background: "var(--social-primary)", color: "white", border: "none", padding: "10px 16px", borderRadius: "999px", cursor: "pointer", fontWeight: "600" }}>
                Envoyer
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function NotificationsView({ notifications, readNotification }) {
  return (
    <section className="social-panel">
      <div className="section-heading">
        <h2>Notifications</h2>
      </div>
      
      {notifications.length === 0 ? (
        <EmptyState title="Aucune notification." />
      ) : (
        <div className="notification-list">
          {notifications.map((n) => {
             const actorName = getAuthorName(n);
             const avatarUrl = getAvatarUrl(n);

             return (
              <button
                key={n.id}
                className={n.read_at ? "" : "unread"}
                onClick={() => readNotification(n)}
                style={{ display: "flex", alignItems: "center", gap: "16px", padding: "16px", background: n.read_at ? "transparent" : "rgba(37,99,235,0.05)" }}
              >
                <AvatarCircle src={avatarUrl} name={actorName} size={48} />
                <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                  <strong style={{ fontSize: "1rem" }}>{n.title}</strong>
                  <span style={{ fontSize: "0.9rem", color: "var(--social-text-muted)" }}>{n.body || n.type}</span>
                  <small style={{ fontSize: "0.75rem", color: "#64748b" }}>{formatDate(n.created_at)}</small>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

function ReelModal({
  reel,
  onClose,
  currentUser,
  comments,
  commentsOpen,
  commentValue,
  setCommentValue,
  setReaction,
  toggleComments,
  submitComment,
  deleteComment,
}) {
  if (!reel) return null;
  const video = reel.media?.find((m) => m.media_type === "video");
  const myReaction = REACTIONS.find((r) => r.id === reel.my_reaction) || REACTIONS[0];
  const authorName = getAuthorName(reel);
  const avatarUrl = getAvatarUrl(reel);

  return (
    <div className="reel-modal-backdrop" onClick={onClose}>
      <div className="reel-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="reel-modal-close" onClick={onClose}>
          ×
        </button>
        <div className="reel-modal-layout">
          <div className="reel-video-container">
            {video ? (
              <video
                src={mediaSrc(video.url)}
                controls
                autoPlay
                className="reel-full-video"
              />
            ) : (
              <div style={{ color: "white" }}>Aucune vidéo trouvée.</div>
            )}
          </div>
          <div className="reel-details-container">
            <header className="post-header" style={{ marginBottom: "16px" }}>
              <AvatarCircle src={avatarUrl} name={authorName} size={48} />
              <div style={{ display: "flex", flexDirection: "column" }}>
                <strong style={{ fontSize: "1.1rem" }}>{authorName}</strong>
                <span style={{ fontSize: "0.85rem", color: "var(--social-text-muted)" }}>{formatDate(reel.created_at)}</span>
              </div>
            </header>
            
            <div className="reel-text-content">
              {reel.content && <p style={{ fontSize: "1rem", lineHeight: "1.5" }}>{reel.content}</p>}
            </div>

            <div className="post-stats" style={{ marginTop: "auto", borderTop: "1px solid rgba(255,255,255,0.1)", paddingTop: "12px" }}>
              <span>{reel.reaction_count || 0} réactions</span>
            </div>

            <div className="post-actions" style={{ marginBottom: "16px" }}>
              <div className="reaction-wrapper">
                <button
                  type="button"
                  onClick={() => setReaction(reel, reel.my_reaction || "like")}
                  style={{
                    color: reel.my_reaction ? myReaction.color : "inherit",
                  }}
                >
                  <span className="reaction-emoji">
                    {reel.my_reaction ? myReaction.emoji : "👍"}
                  </span>
                </button>
              </div>
              <button type="button" onClick={() => toggleComments(reel.id)}>
                <MessageCircle size={20} />
              </button>
            </div>
            
            <div className="reel-comments-scroll">
              {comments.map((c) => {
                const commentAuthorName = getAuthorName(c);
                const commentAvatarUrl = getAvatarUrl(c);
                return (
                  <div className="comment-item" key={c.id}>
                    <AvatarCircle src={commentAvatarUrl} name={commentAuthorName} size={32} />
                    <div className="comment-body">
                      <strong>{commentAuthorName}</strong>
                      <p>{c.content}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="comment-form">
              <input
                value={commentValue}
                onChange={(e) => setCommentValue(e.target.value)}
                placeholder="Ajouter un commentaire..."
                style={{ background: "rgba(255,255,255,0.1)" }}
              />
              <button type="button" onClick={() => submitComment(reel.id)}>
                <Send size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
